import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import { faGripLinesVertical } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useGetChargebackCaseQuery, useRegenerateDefenseDocumentMutation } from '../services/chargebackApi';
import { formatQueryError } from '../services/queryError';
import { ActionPanel } from '../components/ActionPanel';
import { ActivityTimeline } from '../components/ActivityTimeline';
import { ApaleoStayCard } from '../components/ApaleoStayCard';
import { AutohostDetailsCard } from '../components/AutohostDetailsCard';
import { CaseHeader } from '../components/CaseHeader';
import { ChargebackInfoCard } from '../components/ChargebackInfoCard';
import { DefenseDetailsCard } from '../components/DefenseDetailsCard';
import { DefenseDocPanel } from '../components/DefenseDocPanel';
import { PaymentDetailsCard } from '../components/PaymentDetailsCard';
import { PaymentEventsCard } from '../components/PaymentEventsCard';
import { useMediaQuery } from '../util/useMediaQuery';
import { useResizableSplit } from '../util/useResizableSplit';
// Operational statuses that have an open HITL gate for the operator to act on.
const PENDING_REVIEW_STATUSES = new Set([
    'AWAITING_APALEO_DETAILS_RESOLUTION',
    'REVIEWING_DEFENSE_DOC',
    'AWAITING_MANUAL_INTERVENTION'
]);
const POLL_INTERVAL_MS = 5000;
// Tailwind's `lg` breakpoint — the split PDF + cards layout is only comfortable
// above this width; below it we stack.
const SPLIT_MEDIA_QUERY = '(min-width: 1024px)';
export const CaseDetails = () => {
    const { pspReference } = useParams();
    const [selectedVersion, setSelectedVersion] = useState();
    const [regenerating, setRegenerating] = useState(false);
    const isLg = useMediaQuery(SPLIT_MEDIA_QUERY);
    const { ratio, containerRef, onDragStart, onTouchStart, onKeyDown } = useResizableSplit(0.66, 'chargebackDefender.reviewSplit');
    // Snapshot of the highest version we knew before regenerate started; the
    // poll loop watches for a version strictly greater than this.
    const baselineVersionRef = useRef();
    const { data: caseData, isLoading: loading, error: queryError } = useGetChargebackCaseQuery(pspReference ? { pspReference, version: selectedVersion } : skipToken, {
        // Polling drives the regenerate wait — Bedrock + PDF render typically
        // completes in 30-60s; the effect below stops polling once the version
        // advances past the pre-regenerate baseline.
        pollingInterval: regenerating ? POLL_INTERVAL_MS : 0
    });
    const error = queryError ? formatQueryError(queryError) : undefined;
    const [regenerateDefenseDocument] = useRegenerateDefenseDocumentMutation();
    // Stop polling once a new document version lands.
    useEffect(() => {
        if (!regenerating || !caseData) {
            return;
        }
        const baseline = baselineVersionRef.current ?? 0;
        const latest = caseData.currentDocumentVersion ?? 0;
        if (latest > baseline) {
            setRegenerating(false);
            setSelectedVersion(undefined);
        }
    }, [regenerating, caseData]);
    const onSelectVersion = useCallback((version) => {
        setSelectedVersion(version);
    }, []);
    const onRequestRevision = useCallback(async (note) => {
        if (!pspReference || !caseData) {
            return;
        }
        baselineVersionRef.current = caseData.currentDocumentVersion ?? 0;
        await regenerateDefenseDocument({
            pspReference,
            operatorNote: note
        }).unwrap();
        setRegenerating(true);
    }, [caseData, pspReference, regenerateDefenseDocument]);
    if (!pspReference) {
        return null;
    }
    if (loading) {
        return _jsx("p", { className: "p-8", children: "Loading\u2026" });
    }
    if (error && !caseData) {
        return _jsxs("p", { className: "p-8 text-red-600", children: ["Error: ", error] });
    }
    if (!caseData) {
        return _jsx("p", { className: "p-8", children: "No case data." });
    }
    const isReviewMode = !!caseData.defenseStatus &&
        PENDING_REVIEW_STATUSES.has(caseData.defenseStatus) &&
        !caseData.defenseResult;
    const versions = caseData.defenseDocumentVersions ?? [];
    const currentVersion = caseData.currentDocumentVersion ??
        versions.reduce((max, v) => v.supersededByVersion === undefined &&
            (max === undefined || v.version > max)
            ? v.version
            : max, undefined);
    const hasPdf = !!caseData.signedDocumentUrl || versions.length > 0;
    const useSplit = isLg && hasPdf;
    const supportingCards = (_jsxs(_Fragment, { children: [_jsx(ChargebackInfoCard, { caseData: caseData }), _jsx(DefenseDetailsCard, { caseData: caseData, schemeReasonInfo: caseData.schemeReasonInfo }), _jsx(ApaleoStayCard, { caseData: caseData }), _jsx(PaymentDetailsCard, { details: caseData.adyenTransactionDetails }), _jsx(PaymentEventsCard, { pspReference: pspReference }), _jsx(AutohostDetailsCard, { additionalEvidence: caseData.additionalEvidence })] }));
    const reviewActions = (_jsx("div", { id: "review-panel", children: _jsx(ActionPanel, { pspReference: pspReference, defenseStatus: caseData.defenseStatus, currentVersion: currentVersion ?? 1, regenerating: regenerating, onRequestRevision: onRequestRevision }) }));
    const defenseDocPanel = (_jsx(DefenseDocPanel, { signedDocumentUrl: caseData.signedDocumentUrl, versions: versions, selectedVersion: selectedVersion, onSelectVersion: onSelectVersion, currentVersion: currentVersion, regenerating: regenerating }));
    const header = _jsx(CaseHeader, { caseData: caseData });
    const dividerClassName = [
        'group relative w-1 mx-2 bg-base-300 hover:bg-primary',
        'cursor-col-resize rounded flex items-center justify-center'
    ].join(' ');
    const dividerGripClassName = [
        'absolute inline-flex items-center justify-center h-8 w-6 rounded-md',
        'border bg-base-100 shadow-sm text-base-content/60',
        'group-hover:text-primary group-hover:border-primary transition-colors'
    ].join(' ');
    const splitDivider = (_jsx("div", { role: "separator", "aria-orientation": "vertical", "aria-label": "Resize review panels", "aria-valuemin": 30, "aria-valuemax": 85, "aria-valuenow": Math.round(ratio * 100), tabIndex: 0, onMouseDown: onDragStart, onTouchStart: onTouchStart, onKeyDown: onKeyDown, className: dividerClassName, children: _jsx("span", { "aria-hidden": "true", className: dividerGripClassName, children: _jsx(FontAwesomeIcon, { icon: faGripLinesVertical, className: "text-xs" }) }) }));
    const errorBanner = error ? (_jsx("div", { className: "alert alert-error text-sm my-3", children: _jsxs("span", { children: ["Error: ", error] }) })) : null;
    if (isReviewMode && useSplit) {
        const pdfPct = `${(ratio * 100).toFixed(2)}%`;
        const cardsPct = `${((1 - ratio) * 100).toFixed(2)}%`;
        return (_jsxs("div", { className: "max-w-[1600px] mx-auto p-4", children: [header, errorBanner, _jsxs("div", { ref: containerRef, className: "flex mt-4 gap-0 items-stretch", style: { minHeight: 'calc(100vh - 220px)' }, children: [_jsx("div", { style: { width: pdfPct }, className: "min-w-0", children: defenseDocPanel }), splitDivider, _jsxs("div", { style: { width: cardsPct }, className: "min-w-0 space-y-3 overflow-y-auto pb-32", children: [supportingCards, reviewActions] })] })] }));
    }
    if (isReviewMode) {
        // Stacked review mode — cards on top, defense document below them, review
        // actions pinned at the bottom.
        return (_jsxs("div", { className: "max-w-6xl mx-auto p-6 space-y-4", children: [header, errorBanner, supportingCards, hasPdf ? defenseDocPanel : null, reviewActions] }));
    }
    // Overview mode — split honored above lg when the operator has a PDF to
    // read; otherwise stacked cards + standalone defense document + timeline.
    if (useSplit) {
        const pdfPct = `${(ratio * 100).toFixed(2)}%`;
        const cardsPct = `${((1 - ratio) * 100).toFixed(2)}%`;
        return (_jsxs("div", { className: "max-w-[1600px] mx-auto p-4", children: [header, errorBanner, _jsxs("div", { ref: containerRef, className: "flex mt-4 gap-0 items-stretch", style: { minHeight: 'calc(100vh - 220px)' }, children: [_jsx("div", { style: { width: pdfPct }, className: "min-w-0", children: defenseDocPanel }), splitDivider, _jsx("div", { style: { width: cardsPct }, className: "min-w-0 space-y-3 overflow-y-auto pb-8", children: supportingCards })] }), _jsx("div", { className: "mt-4", children: _jsx(ActivityTimeline, { caseData: caseData }) })] }));
    }
    return (_jsxs("div", { className: "max-w-6xl mx-auto p-6 space-y-4", children: [header, error ? (_jsx("div", { className: "alert alert-error text-sm", children: _jsxs("span", { children: ["Error: ", error] }) })) : null, supportingCards, hasPdf ? defenseDocPanel : null, _jsx(ActivityTimeline, { caseData: caseData })] }));
};
